import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Loader2 } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { getUserByid, updateProfilePicture, updateUserProfile } from "../../reducers/authentication.slice";
import AddressAutocomplete from "../../components/AddressAutocompleteComponent/GoogleAddressInput";
import stateandcities from "../../assests/stateAndCities/stateandcities";
import { base64ToFile, fileToBase64 } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { stringToColor } from "../../components/member/tierConfig";
import PageHeader from "../../components/shared/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useMediaQuery from "../../components/shared/useMediaQuery";

// The original page also supports fetching the profile via API
// (`location.state.isPaymentVia`) and scrolling to a section
// (`location.state.isScroll`), but the only real caller — MemberProfilePage's
// Edit button — never sets either flag, so those paths are dead here.
// Verified via a repo-wide grep before dropping them.

function FieldLabel({ children, required }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: "#c3c8cc", marginBottom: 6 }}>
      {children}
      {required && <span style={{ color: "#f27059" }}> *</span>}
    </div>
  );
}

const EMPTY_ADDRESS = { address: "", building: "", city: "", state: "", zipcode: "" };

export default function MemberEditProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [profilePic, setProfilePic] = useState("");
  const [user, setUser] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    role: "",
    discord_role: "",
    first_name: "",
    last_name: "",
    discord_id: "",
    email: "",
    status: "",
    address_details: EMPTY_ADDRESS,
    // Not edited in this UI, but the update endpoint does a full-object
    // write — carried through unchanged so it isn't wiped out on save.
    payment_details: {},
  });

  useEffect(() => {
    const newUserData = {
      ...getUsers(),
      discord_id: location.state?.user_detail?.discord_id,
      discord_role: location.state?.user_detail?.discord_role,
      permission_details: location.state?.user_detail?.permission_details,
      address_details: location.state?.user_detail?.address_details,
      payment_details: location.state?.user_detail?.payment_details,
      first_name: location.state?.user_detail?.first_name,
      last_name: location.state?.user_detail?.last_name,
      profile_picture: location.state?.user_detail?.profile_picture,
      role: location.state?.user_detail?.role,
      ip_address: location.state?.user_detail?.ip_address,
      last_login: location.state?.user_detail?.last_login,
      date_of_joining: location.state?.user_detail?.date_of_joining,
      login_type: location.state?.user_detail?.login_type,
    };
    setUser(newUserData);
    localStorage.setItem("user", JSON.stringify(newUserData));
    setFormData({
      id: newUserData?.id || "",
      first_name: newUserData?.first_name || "",
      last_name: newUserData?.last_name || "",
      role: newUserData?.role || "",
      discord_id: newUserData?.discord_id || "",
      discord_role: newUserData?.discord_role?.[0] || "",
      email: newUserData?.email || "",
      address_details: {
        address: newUserData?.address_details?.address || "",
        building: newUserData?.address_details?.building || "",
        city: newUserData?.address_details?.city || "",
        state: newUserData?.address_details?.state || "",
        zipcode: newUserData?.address_details?.zipcode || "",
      },
      status: newUserData?.status || "",
      payment_details: newUserData?.payment_details || {},
    });
    setProfilePic(newUserData?.profile_picture || "");
  }, []);

  const handleFormData = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressAutocompleteChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      address_details: {
        ...prev.address_details,
        address: value?.street,
        city: value?.city,
        state: value?.state,
        zipcode: value?.zip,
      },
    }));
  };

  const handleAddressChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      address_details: { ...prev.address_details, [name]: value },
    }));
  };

  const handleUpdateUserProfile = () => {
    const payload = { ...formData };
    delete payload.discord_role;
    delete payload.email;
    delete payload.role;
    delete payload.status;
    // Not edited in this UI. Backend treats a missing/falsy payment_details
    // as "leave unchanged", but an empty {} fails schema validation because
    // PaymentDetails has required fields (payment_method, account_holder_name,
    // email) with no defaults — so it must be omitted, not sent as {}.
    delete payload.payment_details;

    setSaving(true);
    dispatch(updateUserProfile(payload)).then((res) => {
      setSaving(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      navigate("/dashboard/my-profile");
    });
  };

  const handleUpdateProfilePicture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    dispatch(updateProfilePicture({ user_id: user?.id, file })).then(async (res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setUploadingPic(false);
        showToast(res?.error?.message, "error");
        event.target.value = null;
        return;
      }
      const base64Img = await fileToBase64(file);
      setProfilePic(base64Img);
      localStorage.setItem("user", JSON.stringify({ ...getUsers(), profile_picture: base64Img }));
      setUploadingPic(false);
      event.target.value = null;
      showToast("Profile picture uploaded successfully", "success");
    });
  };

  const isUnchanged =
    formData.first_name === user?.first_name &&
    formData.last_name === user?.last_name &&
    formData.email === user?.email &&
    formData.address_details?.address === user?.address_details?.address &&
    formData.address_details?.building === user?.address_details?.building &&
    formData.address_details?.city === user?.address_details?.city &&
    formData.address_details?.state === user?.address_details?.state &&
    formData.address_details?.zipcode === user?.address_details?.zipcode;

  const initials = `${formData.first_name?.[0] || ""}${formData.last_name?.[0] || ""}`.toUpperCase();
  const profileImgUrl = profilePic ? URL.createObjectURL(base64ToFile(profilePic, "file", "image/*")) : null;

  return (
    <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", display: "flex", flexDirection: "column", gap: isMobile ? 16 : 20 }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          width: "fit-content",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <PageHeader title="Edit Profile" />

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? 18 : 22,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 14 : 18,
        }}
      >
        <div
          style={{
            width: 62,
            height: 62,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.15)",
            background: profileImgUrl ? "transparent" : stringToColor(`${formData.first_name} ${formData.last_name}` || "RSC"),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {profileImgUrl ? (
            <img src={profileImgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>{initials || "?"}</span>
          )}
        </div>
        <div style={{ width: isMobile ? "100%" : "auto" }}>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="profile-pic-upload"
            type="file"
            onChange={handleUpdateProfilePicture}
          />
          <label htmlFor="profile-pic-upload">
            <Button variant="outline" size="sm" asChild>
              <span style={{ cursor: "pointer", width: isMobile ? "100%" : "auto", justifyContent: "center" }}>
                {uploadingPic ? <Loader2 size={13} className="tw-animate-spin" /> : "Change Photo"}
              </span>
            </Button>
          </label>
        </div>
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? 18 : 22,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff" }}>Basic Information</div>

        <div>
          <FieldLabel required>First Name</FieldLabel>
          <Input type="text" name="first_name" value={formData.first_name} placeholder="First Name" onChange={handleFormData} />
        </div>
        <div>
          <FieldLabel required>Last Name</FieldLabel>
          <Input type="text" name="last_name" value={formData.last_name} placeholder="Last Name" onChange={handleFormData} />
        </div>
        <div>
          <FieldLabel>Discord Username</FieldLabel>
          <Input type="text" value={formData.discord_id} disabled placeholder="Discord username" />
        </div>
        <div>
          <FieldLabel>Discord Role</FieldLabel>
          <Input type="text" value={formData.discord_role || "No Role"} disabled />
        </div>
        <div>
          <FieldLabel required>Email Address</FieldLabel>
          <Input type="email" value={formData.email} disabled />
        </div>
        <div>
          <FieldLabel>Address</FieldLabel>
          <AddressAutocomplete onChange={handleAddressAutocompleteChange} defaultValue={user?.address_details} />
        </div>
        <div>
          <FieldLabel>Apartment/Suite (Optional)</FieldLabel>
          <Input
            type="text"
            placeholder="Apartment/Suite"
            value={formData.address_details?.building}
            onChange={(e) => handleAddressChange("building", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>State</FieldLabel>
          <select
            value={formData.address_details?.state}
            onChange={(e) => handleAddressChange("state", e.target.value)}
            style={{
              width: "100%",
              height: 36,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
              color: "#fff",
              fontSize: 13,
              padding: "0 10px",
            }}
          >
            <option value="" style={{ color: "#000" }}>
              Select state
            </option>
            {stateandcities.map((state, index) => (
              <option key={index} value={state.value} style={{ color: "#000" }}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel>City</FieldLabel>
          <Input
            type="text"
            placeholder="City"
            value={formData.address_details?.city}
            onChange={(e) => handleAddressChange("city", e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Zip Code</FieldLabel>
          <Input
            type="text"
            placeholder="Zip Code"
            value={formData.address_details?.zipcode}
            onChange={(e) => handleAddressChange("zipcode", e.target.value)}
          />
        </div>
      </div>

      <Button disabled={isUnchanged || saving} onClick={handleUpdateUserProfile} style={{ width: isMobile ? "100%" : "auto" }}>
        {saving ? <Loader2 size={14} className="tw-animate-spin" /> : "Save"}
      </Button>
    </div>
  );
}
